import React, { useState } from 'react';
import styled from 'styled-components';
import { AlertCircle, Clock, Flame, GripVertical, IdCard, Lock, MessageSquareText, Plus, ShoppingCart } from 'lucide-react';
import currency from 'helpers/currency';
import date from 'helpers/date';
import { tone, TONE, toneForPercent, isSellingOut } from '../detail/_sales/salesTokens';
import { currentLotIndex, isTicketFree, lotsOf, ticketCapacity, ticketCommitted } from './ticketModel';
import { hasSaleStart } from './purchaseRules';
import { Bar, Chip, ERROR, INK, INK_SOFT, LINE, MUTED, OutlineButton, accent, accentAlpha } from './ticketFormStyles';

// "Esgotando" tem tom próprio para não disputar o âmbar de "vendendo devagar".
const SELLING_OUT = { text: '#9A3D0C', fill: '#D9692A', track: '#FBE1D2', soft: '#FFF3EC', border: '#F4CDB5' };
const ISSUE = { text: ERROR, soft: '#FCEEEE', border: '#F1C4C1' };

const TONE_LABEL = {
    [TONE.NONE]: 'Sem vendas',
    [TONE.SLOW]: 'Devagar',
    [TONE.SELLING]: 'Vendendo',
    [TONE.STRONG]: 'Vendendo bem',
    [TONE.SOLD_OUT]: 'Esgotado',
};

/**
 * O chip do item. Pendência ganha de tudo, porque é o que impede salvar; depois
 * vem o que tira o ingresso da vitrine (inativo, oculto); por último, o ritmo de
 * venda, na mesma escala de cor da aba Ingressos do evento.
 */
function statusOf(ticket, issueCount) {
    if (issueCount) {
        return {
            label: issueCount === 1 ? '1 pendência' : `${issueCount} pendências`,
            colors: ISSUE,
            icon: <AlertCircle size={12} aria-hidden='true' />,
        };
    }

    if (!ticket.id) return { label: 'Novo', colors: tone[TONE.NONE] };

    const anyLotOpen = lotsOf(ticket).some(lot => lot.serverActive);

    if (!ticket.is_active && !anyLotOpen) return { label: 'Inativo', colors: tone[TONE.NONE], dashed: true };

    if (ticket.hidden_code) {
        return { label: 'Oculto', colors: tone[TONE.NONE], icon: <Lock size={12} aria-hidden='true' /> };
    }

    const capacity = ticketCapacity(ticket);
    const percent = capacity ? (ticketCommitted(ticket) / capacity) * 100 : 0;

    if (isSellingOut(percent)) {
        return { label: 'Esgotando', colors: SELLING_OUT, icon: <Flame size={12} aria-hidden='true' /> };
    }

    const level = toneForPercent(percent);

    return { label: TONE_LABEL[level], colors: tone[level] };
}

function barColors(ticket, percent) {
    if (isSellingOut(percent)) return SELLING_OUT;

    return tone[toneForPercent(percent)];
}

function metaOf(ticket, isEventFree) {
    const lots = lotsOf(ticket);
    const current = currentLotIndex(ticket);
    const shown = lots[current >= 0 ? current : 0];
    const free = isTicketFree(ticket, isEventFree);
    const parts = [];

    if (free) parts.push('Gratuito');
    else if (Number(shown.floatPrice) > 0) parts.push(currency.format(shown.floatPrice));
    else parts.push('Sem preço ainda');

    if (current >= 0 && lots.length > 1) parts.push(`Lote ${current + 1} de ${lots.length}`);
    else parts.push(lots.length === 1 ? '1 lote' : `${lots.length} lotes`);

    return parts.join(' · ');
}

export default function TicketList({
    tickets,
    selectedId,
    isEventFree,
    issueCountOf = () => 0,
    dirtyIds,
    onSelect,
    onAdd,
    onMove,
}) {
    const [dragIndex, setDragIndex] = useState(null);
    const [overIndex, setOverIndex] = useState(null);

    function endDrag() {
        setDragIndex(null);
        setOverIndex(null);
    }

    function onGripKeyDown(e, index) {
        if (e.key === 'ArrowUp' && index > 0) {
            e.preventDefault();
            onMove(index, index - 1);
        }

        if (e.key === 'ArrowDown' && index < tickets.length - 1) {
            e.preventDefault();
            onMove(index, index + 1);
        }
    }

    return (
        <Wrapper aria-labelledby='ticket-list-title'>
            <Header>
                <Title id='ticket-list-title'>
                    Ingressos <Count>({tickets.length})</Count>
                </Title>
                <AddButton type='button' onClick={onAdd}>
                    <Plus size={16} aria-hidden='true' />
                    Novo ingresso
                </AddButton>
            </Header>

            <List>
                {tickets.map((ticket, index) => {
                    const issueCount = issueCountOf(ticket);
                    const status = statusOf(ticket, issueCount);
                    const selected = ticket.internal_id === selectedId;
                    const capacity = ticketCapacity(ticket);
                    const committed = ticketCommitted(ticket);
                    const percent = capacity ? (committed / capacity) * 100 : 0;
                    const bar = barColors(ticket, percent);
                    const name = ticket.name || 'Ingresso sem nome';

                    return (
                        <Item
                            key={ticket.internal_id}
                            selected={selected}
                            hasIssue={issueCount > 0}
                            dropTarget={overIndex === index && dragIndex !== null && dragIndex !== index}
                            dragging={dragIndex === index}
                            onDragOver={e => {
                                if (dragIndex === null) return;
                                e.preventDefault();
                                setOverIndex(index);
                            }}
                            onDrop={e => {
                                e.preventDefault();
                                if (dragIndex !== null && dragIndex !== index) onMove(dragIndex, index);
                                endDrag();
                            }}
                        >
                            {tickets.length > 1 && (
                                <Grip
                                    type='button'
                                    draggable
                                    aria-label={`Reordenar ${name}. Use as setas para cima e para baixo.`}
                                    title='Arraste ou use as setas para reordenar'
                                    onKeyDown={e => onGripKeyDown(e, index)}
                                    onDragStart={e => {
                                        e.dataTransfer.effectAllowed = 'move';
                                        e.dataTransfer.setData('text/plain', String(index));
                                        setDragIndex(index);
                                    }}
                                    onDragEnd={endDrag}
                                >
                                    <GripVertical size={16} aria-hidden='true' />
                                </Grip>
                            )}

                            <Pick
                                type='button'
                                aria-current={selected ? 'true' : undefined}
                                padLeft={tickets.length <= 1}
                                onClick={() => onSelect(ticket.internal_id)}
                            >
                                <Row>
                                    <NameWrap>
                                        <Name muted={!ticket.name}>{name}</Name>
                                        {dirtyIds.has(ticket.internal_id) && (
                                            <DirtyDot title='Alterado, ainda não salvo'>
                                                <span>Alterado, ainda não salvo</span>
                                            </DirtyDot>
                                        )}
                                    </NameWrap>
                                    <Chip
                                        bg={status.colors.soft}
                                        fg={status.colors.text}
                                        border={status.colors.border}
                                        dashed={status.dashed}
                                    >
                                        {status.icon}
                                        {status.label}
                                    </Chip>
                                </Row>

                                <Meta>
                                    <span>{metaOf(ticket, isEventFree)}</span>
                                    {ticket.has_max_per_order && ticket.max_per_order && (
                                        <Rule><ShoppingCart size={13} aria-hidden='true' />Máx. {ticket.max_per_order}</Rule>
                                    )}
                                    {ticket.requires_document && (
                                        <Rule><IdCard size={13} aria-hidden='true' />CPF</Rule>
                                    )}
                                    {hasSaleStart(ticket) && ticket.opens_at && (
                                        <Rule><Clock size={13} aria-hidden='true' />Abre {date.formatDateTime(ticket.opens_at)}</Rule>
                                    )}
                                    {ticket.buyer_message && (
                                        <Rule><MessageSquareText size={13} aria-hidden='true' />Recado</Rule>
                                    )}
                                </Meta>

                                {committed > 0 || ticket.id ? (
                                    <Progress>
                                        <Bar track={bar.track} fill={bar.fill} percent={percent} />
                                        <ProgressText>{committed}/{capacity}</ProgressText>
                                    </Progress>
                                ) : null}
                            </Pick>
                        </Item>
                    );
                })}
            </List>
        </Wrapper>
    );
}

const Wrapper = styled.section`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: ${INK};
`;

const Count = styled.span`
    font-weight: 400;
    color: ${MUTED};
`;

const AddButton = styled(OutlineButton)`
    min-height: 40px;
    padding: 0 12px;
    color: ${accent};
    border-color: ${accentAlpha('40')};
`;

const List = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Item = styled.li.withConfig({
    shouldForwardProp: prop => !['selected', 'hasIssue', 'dropTarget', 'dragging'].includes(prop),
})`
    display: flex;
    align-items: stretch;
    background: ${({ selected }) => (selected ? '#FBF8FF' : '#fff')};
    border: ${({ selected }) => (selected ? 2 : 1)}px solid ${({ selected, hasIssue }) => (
        hasIssue ? ERROR : selected ? accent : LINE
    )};
    border-radius: 14px;
    overflow: hidden;
    opacity: ${({ dragging }) => (dragging ? 0.5 : 1)};
    box-shadow: ${({ dropTarget }) => (dropTarget ? `0 -3px 0 0 ${accent}` : 'none')};
    transition: border-color 0.15s ease, background 0.15s ease;
`;

const Grip = styled.button`
    width: 32px;
    flex-shrink: 0;
    border: 0;
    padding: 0;
    background: transparent;
    color: #8A8A93;
    cursor: grab;
    display: flex;
    align-items: center;
    justify-content: center;

    &:focus-visible {
        outline: 2px solid ${accent};
        outline-offset: -2px;
        color: ${accent};
    }
`;

const Pick = styled.button.withConfig({
    shouldForwardProp: prop => prop !== 'padLeft',
})`
    flex: 1 1 auto;
    min-width: 0;
    text-align: left;
    background: transparent;
    border: 0;
    padding: 12px 14px 12px ${({ padLeft }) => (padLeft ? '14px' : '2px')};
    cursor: pointer;
    font-family: inherit;
    color: ${INK};
    display: flex;
    flex-direction: column;
    gap: 6px;

    &:focus-visible {
        outline: 2px solid ${accent};
        outline-offset: -2px;
        border-radius: 12px;
    }
`;

const Row = styled.span`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const NameWrap = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
`;

const Name = styled.span.withConfig({
    shouldForwardProp: prop => prop !== 'muted',
})`
    font-size: 15px;
    font-weight: 600;
    color: ${({ muted }) => (muted ? MUTED : INK)};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DirtyDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${accent};
    flex-shrink: 0;

    span {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
    }
`;

const Meta = styled.span`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 10px;
    font-size: 13px;
    color: ${INK_SOFT};
`;

const Rule = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    color: ${MUTED};
`;

const Progress = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ProgressText = styled.span`
    font-size: 12px;
    color: ${INK_SOFT};
    white-space: nowrap;
`;
